import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toArabicWord } from "number-to-arabic-words/dist/index-node.js";
import { toWords } from "number-to-words";
import { useReactToPrint } from "react-to-print";

// Assets
import saveIcon from "../../assets/save_icon.png";

// Components
import PageWrapper from "../../components/PageWrapper";
import Dropdown from "../../components/Dropdown";
import DiscountSection from "../../components/DiscountSection";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import Loader from "../../components/Loader";
import HotCustomInput from "../../components/HotCustomInput";
import PageHeadline from "../../components/PageHeadline";
import ErrorBox from "../../components/ErrorBox";
import PaperListedTable from "./PaperListedTable";

// Utils
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import classes from "../../components/Modal.module.css";
import { DataContext } from "../../utils/DataContext";
import { formatDate } from "../../utils/toDate";
import PaperInvoicePrintComponent from "./PaperInvoicePrint";
import SipparLightsInvoiceTemplate from "./SipparLightsInvoiceTemplate";

const PaperCustomersCashier = ({ data }) => {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();
  const { getCustomers } = useContext(DataContext);

  // States
  const [errorFields, setErrorFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProductName, setLoadingProductName] = useState(false);
  const [loadingInvoiceNumber, setLoadingInvoiceNumber] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [sumOfTotalAmount, setSumOfTotalAmount] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [price, setPrice] = useState(0);

  const spanStyles = currentDirection === "rtl" ? classes.shortSpan : null;

  // Receipt reference for printing
  const receiptRef = useRef(null);
  const sipparInvoiceRef = useRef(null);

  // Invoice related states
  const [selectedInvoiceType, setSelectedInvoiceType] = useState({
    title: "debt",
    value: "debt",
  });

  const [invoice, setInvoice] = useState({
    id: "",
    date: new Date(),
    discount: 0,
    receivedAmount: 0,
    leftAmount: 0,
    notes: "",
    documentPath: "",
  });

  // Customer state
  const [selectedCustomer, setSelectedCustomer] = useState({
    id: "",
    name: "",
    phoneNumber: "",
    address: "",
  });

  const [selectedCurrency, setSelectedCurrency] = useState({
    title: "usd",
    value: "USD",
  });

  const [sellError, setSellError] = useState({ status: "", invalidColors: [] });

  // Table columns configuration
  const columns = [
    { key: "rollCode", title: "categoryName", keepOnMobile: true },
    // { key: "categoryName", title: "rollCode", keepOnMobile: true },
    { key: "dimension", title: "dimension", keepOnMobile: true },
    { key: "paperThickness", title: "paperThickness", keepOnMobile: true },
    { key: "amountOfRolls", title: "amountOfRolls", keepOnMobile: true },
    { key: "quantity", title: "quantity", keepOnMobile: false },
    { key: "price", title: "price", keepOnMobile: true },
    { key: "totalAmount", title: "totalAmount", keepOnMobile: true },
  ];

  // Currency options
  const currencies = [
    { title: "iqd", value: "IQD" },
    { title: "usd", value: "USD" },
  ];

  const handlePrintSipparLights = useReactToPrint({
    contentRef: sipparInvoiceRef,
    pageStyle: `
    @page {
      margin: 0 !important;
      size: A4;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        direction: ${currentDirection};
        width: 100% !important;
        height: 100% !important;
      }
      body {
        -webkit-print-color-adjust: exact;
        direction: ${currentDirection};
        margin: 0 !important;
        padding: 0 !important;
      }
      .no-print {
        display: none !important;
      }
    }
  `,
  });

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    pageStyle: `
    @page {
      margin: 0 !important;
      size: A4;
    }
    @media print {
      * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        direction: ${currentDirection};
        width: 100% !important;
        height: 100% !important;
      }
      body {
        -webkit-print-color-adjust: exact;
        direction: ${currentDirection};
        margin: 0 !important;
        padding: 0 !important;
      }
      .no-print {
        display: none !important;
      }
    }
  `,
  });

  // Field validation
  const validateFields = () => {
    setIsSubmitting(true);
    let errors = {};
    let hasError = false;

    if (!invoice.date) {
      errors["date"] = t("validDateRequired");
      hasError = true;
    }

    const rowsWithData = tableData.filter(
      (item) =>
        item.rollCode ||
        item.categoryName ||
        item.quantity ||
        item.price ||
        item.totalAmount
    );

    if (rowsWithData.length === 0) {
      errors["tableData"] = t("atLeastOneItemRequired");
      hasError = true;
    } else {
      // Check if any row with data has missing fields
      const incompleteRows = rowsWithData.filter(
        (item) =>
          !(
            item.rollCode &&
            item.categoryName &&
            item.quantity &&
            item.price &&
            item.totalAmount
          )
      );

      if (incompleteRows.length > 0) {
        errors["tableItems"] = t("pleaseCompleteRowData");
        hasError = true;
      }
    }

    setErrorFields(errors);
    return !hasError;
  };

  // Fetch color name by code
  const fetchColorName = async (colorCode, rowIndex, currentRows, setRows) => {
    try {
      setLoadingProductName(true);

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/sellFabricColor/getColorName`,
        { colorCode },
        {
          headers: {
            Authorization: `bearer ${userData?.token}`,
            "Organization-Id": userData?.organizationId,
          },
        }
      );

      if (response.data) {
        const updatedRows = [...currentRows];
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          categoryName: response.data.colorName,
          price: response.data.price || 0,
          dimension: response.data.dimension || "",
          paperThickness: response.data.paperThickness || 0,
          fabricRulerId: response.data.fabricRulerId,
        };

        setErrorFields({});

        setRows(updatedRows);
      }
    } catch (error) {
      console.error("Failed to fetch color name:", error);
      // Handle error case
      const updatedRows = [...currentRows];
      updatedRows[rowIndex] = {
        ...updatedRows[rowIndex],
        rollCode: "",
        categoryName: "",
        price: 0,
        quantity: 0,
        totalAmount: 0,
      };
      setRows(updatedRows);
      setTableData(updatedRows);

      // Show error message
      setErrorFields((prev) => ({
        ...prev,
        tableItems: t("colorNotFound"),
      }));
    } finally {
      setLoadingProductName(false);
    }
  };

  // Get new invoice number
  const getNewInvoiceNumber = async () => {
    try {
      setLoadingInvoiceNumber(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/sellFabricColor`,
        {
          params: {
            organizationId: userData?.organizationId,
          },
        }
      );

      setInvoice((prev) => ({
        ...prev,
        id: response?.data?.invoiceNumber,
        documentPath: response?.data?.document?.[0]?.path,
      }));
    } catch (error) {
      console.error("Failed to get a new invoice number");
    } finally {
      setLoadingInvoiceNumber(false);
    }
  };

  const isRowEmpty = (row) => {
    return (
      !row.rollCode &&
      !row.categoryName &&
      !row.quantity &&
      !row.price &&
      !row.totalAmount
    );
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: value }));

    if (name === "phoneNumber" || name === "address") {
      setSelectedCustomer((prev) => ({ ...prev, [name]: value }));
    }

    if (errorFields[name]) {
      setErrorFields((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!validateFields()) {
      return;
    }

    setSellError({ status: "", invalidColors: [] });

    try {
      setIsLoading(true);

      // Filter out empty rows
      const filteredTableData = tableData.filter(
        (item) =>
          item.rollCode &&
          item.categoryName &&
          item.quantity &&
          item.price &&
          item.totalAmount
      );

      const payload = {
        // Invoice identifier
        id: invoice.id,

        // Customer info
        customerName: selectedCustomer.name,
        customerPhoneNumber: selectedCustomer.phoneNumber,
        customerAddress: selectedCustomer.address,
        currency: selectedCurrency.value,

        // Invoice details
        date: new Date(invoice.date),
        notes: invoice.notes,
        totalPrice: originalPrice || 0,
        finalAmount: parseFloat(sanitizeNumber(price)) || 0,
        receivedAmount:
          parseFloat(sanitizeNumber(invoice?.receivedAmount)) || 0,
        leftAmount: parseFloat(sanitizeNumber(invoice?.leftAmount)) || 0,
        discount:
          sanitizeNumber(
            parseFloat(parseInt(originalPrice) - parseInt(price))
          ) || 0,
        finalAmountInAr: toArabicWord(price || 0),
        finalAmountInEn: toWords(price || 0),
        invoiceType: selectedInvoiceType.value,
        measuringUnit: "TON",

        // Colors data
        colors: filteredTableData.map((item) => ({
          colorCode: item.rollCode,
          colorName: item.categoryName,
          quantity: parseFloat(sanitizeNumber(item.quantity)) || 0,
          amountOfRolls: parseFloat(sanitizeNumber(item.amountOfRolls)) || 0,
          price: parseFloat(sanitizeNumber(item.price)) || 0,
          fabricRulerId: item.fabricRulerId,
          totalAmount: parseFloat(sanitizeNumber(item.totalAmount)) || 0,
          measuringUnit: "TON",
          currency: selectedCurrency.value,
        })),

        // IDs
        userId: parseInt(userData?.userId),
        organizationId: parseInt(userData?.organizationId),
        customerId: selectedCustomer?.id || null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_URL}/sellFabricColor`,
        payload,
        {
          headers: {
            Authorization: `bearer ${userData?.token}`,
          },
        }
      );

      if (response.status === 201) {
        // Reset states after successful creation
        setTableData([]);
        setSelectedCustomer({
          id: "",
          name: "",
          phoneNumber: "",
          address: "",
        });
        setInvoice({
          id: "",
          date: new Date(),
          discount: 0,
          receivedAmount: 0,
          leftAmount: 0,
          notes: "",
          documentPath: "",
          address: "",
        });
        setErrorFields({});
        setIsSubmitting(false);

        // Get new invoice number and print receipt
        getNewInvoiceNumber();
        const invoice = localStorage.getItem("invoiceType");

        switch (invoice) {
          case "sipparLights":
            handlePrintSipparLights();
            break;
          default:
            handlePrint();
        }
      }
    } catch (error) {
      const errorResponse = error?.response?.data;

      if (errorResponse?.invalidColors?.length > 0) {
        // Use the exact response structure from backend
        setSellError({
          error: "outOfStock",
          invalidColors: errorResponse.invalidColors,
        });
      } else {
        setSellError({
          error: "errorCreatingInvoice",
          invalidColors: [],
        });
      }

      window.scroll(0, 0);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle save and print action
  const handleSaveAndPrint = async () => {
    await handleCreateInvoice();
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    if (customer.name) {
      setErrorFields((prev) => ({
        ...prev,
        customerName: undefined,
      }));
    }
    if (customer.phoneNumber) {
      setErrorFields((prev) => ({
        ...prev,
        phoneNumber: undefined,
      }));
    }
  };

  // Effect to calculate total amount when table data changes
  useEffect(() => {
    const totalSum = tableData.reduce((sum, item) => {
      return sum + (parseFloat(item.totalAmount) || 0);
    }, 0);

    setSumOfTotalAmount(totalSum);
    setOriginalPrice(totalSum);
    setPrice(totalSum);

    // Set amounts based on invoice type
    if (selectedInvoiceType.value === "debt") {
      setInvoice((prev) => ({
        ...prev,
        receivedAmount: 0,
        leftAmount: totalSum,
      }));
    } else {
      setInvoice((prev) => ({
        ...prev,
        receivedAmount: totalSum,
        leftAmount: 0,
      }));
    }

    if (tableData.length > 0 && errorFields.tableData) {
      setErrorFields((prev) => ({
        ...prev,
        tableData: undefined,
      }));
    }
  }, [tableData]);

  // Transform tableData into the expected 2D format for invoiceData
  const printableInvoiceData = tableData.map((item) => [
    { value: item.rollCode || null },
    { value: `${item.amountOfRolls} ${t("roll")}` || "-" },
    { value: `${item.dimension} ${t("cm")}` || "-" },
    { value: `${item.paperThickness} ${t("gram")}` || "-" },
    { value: `${item.quantity} ${t("ton")}` || "-" },
    { value: `${item.price} ${selectedCurrency.value}` || "-" },
    { value: `${item.price * item.quantity} ${selectedCurrency.value}` },
  ]);

  const invoiceDetails = {
    invoiceNumber: invoice?.id,
    invoiceDate: invoice?.date
      ? new Date(invoice.date).toLocaleDateString()
      : "",
    invoiceNote: invoice?.notes || "",
    invoiceAmount: {
      receivedAmount: invoice.receivedAmount,
      totalPrice: originalPrice,
      discount: parseFloat(parseInt(originalPrice) - parseInt(price)),
      finalAmount: price,
      leftAmount: invoice.leftAmount,
    },
  };

  const customerData = {
    customerName: selectedCustomer?.name || "",
    phoneNumber: selectedCustomer?.phoneNumber || "",
    address: selectedCustomer?.address || "",
  };

  const creatorDetails = {
    name: userData?.username || "-",
  };

  useEffect(() => {
    getNewInvoiceNumber();
  }, []);

  useEffect(() => {
    if (selectedInvoiceType.value === "debt") {
      setInvoice((prev) => ({
        ...prev,
        receivedAmount: 0,
        leftAmount: parseFloat(sanitizeNumber(price)) || 0,
      }));
    } else if (selectedInvoiceType.value === "cash") {
      setInvoice((prev) => ({
        ...prev,
        receivedAmount: parseFloat(sanitizeNumber(price)) || 0,
        leftAmount: 0,
      }));
    }
  }, [selectedInvoiceType, price]);

  return (
    <PageWrapper withBackButton>
      <div className="o-column u-padding--bottom_500">
        {sellError?.error && (
          <ErrorBox>
            {t(sellError.error)}
            {sellError?.invalidColors?.map((color, index) => (
              <ul key={index} className="u-margin--top_300">
                <li>
                  {t("color")}: {color?.code}
                </li>

                {/* Display quantity issues if present */}
                {color?.quantity && (
                  <>
                    <li>
                      {t("requestedQuantity")}: {color.quantity.requested}{" "}
                      {t("ton")}
                    </li>
                    <li>
                      {t("availableQuantity")}: {color.quantity.available}{" "}
                      {t("ton")}
                    </li>
                  </>
                )}

                {/* Display rolls issues if present */}
                {color?.rolls && (
                  <>
                    <li>
                      {t("requestedAmount")}: {color.rolls.requested}{" "}
                      {t("roll")}
                    </li>
                    <li>
                      {t("availableAmount")}: {color.rolls.available}{" "}
                      {t("roll")}
                    </li>
                  </>
                )}
              </ul>
            ))}
          </ErrorBox>
        )}
        <PageHeadline
          title={"🛒 " + t("manualCustomersSales")}
          classList="u-margin--top_700 u-margin--bottom_500"
        />
        {/* THE NEW ONE */}
        <section className="o-row o-row--horizontal-align_space-between">
          {/* Column 1 */}
          <div className="o-row o-row--gap-size_300">
            <div className="o-column o-column--gap-size_500 u-padding--top_100">
              <span>{t("customerName")}: </span>
              <span>{t("phoneNumber")}: </span>
              <span>{t("address")}: </span>
              <span className={spanStyles}>{t("currency")}: </span>
            </div>

            <div className="o-column o-column--gap-size_300 o-column--horizontal-align_start">
              <div className="u-width_full-width">
                <HotCustomInput
                  initOptions={[]}
                  selected={selectedCustomer}
                  setSelected={(customer) =>
                    handleSelectCustomer(customer, "noRequest")
                  }
                  optionsName={"customers"}
                  getData={getCustomers}
                  flex
                  errorText={errorFields["customerName"]}
                />
              </div>

              <div className="u-width_full-width">
                <CustomInput
                  name="phoneNumber"
                  value={selectedCustomer.phoneNumber}
                  onChange={handleInputChange}
                  errorText={errorFields["phoneNumber"]}
                  disabled={selectedCustomer?.id}
                />
              </div>
              <div className="u-width_full-width">
                <CustomInput
                  name="address"
                  value={selectedCustomer.address}
                  onChange={handleInputChange}
                  errorText={errorFields["address"]}
                  disabled={selectedCustomer?.id}
                />
              </div>
              <div className="u-width_full-width">
                <Dropdown
                  options={currencies}
                  selected={selectedCurrency}
                  setSelected={setSelectedCurrency}
                  flex
                  disabled={selectedCustomer?.id}
                />
              </div>
            </div>
          </div>
          {/* Column 2 */}
          <div className="o-row o-row--gap-size_300">
            <div className="o-column o-column--gap-size_500 u-padding--top_100">
              <span>{t("invoiceNumber")}: </span>
              <span>{t("Date")}: </span>
              <span className={spanStyles}>{t("invoiceType")}: </span>
            </div>
            <div className="o-column o-row--gap-size_300">
              <div className="u-width_full-width">
                <CustomInput
                  value={invoice?.id}
                  loading={loadingInvoiceNumber}
                />
              </div>
              <div className="u-width_full-width">
                <CustomInput
                  type="date"
                  name="date"
                  value={formatDate(invoice?.date)}
                  onChange={handleInputChange}
                  errorText={errorFields["date"]}
                />
              </div>

              <div className="u-width_full-width">
                <Dropdown
                  options={[
                    { title: "debt", value: "debt" },
                    { title: "cash", value: "cash" },
                  ]}
                  selected={selectedInvoiceType}
                  setSelected={setSelectedInvoiceType}
                  flex
                />
              </div>
            </div>
          </div>
        </section>
        {/* Table section */}
        <section className="u-margin--top_500 u-margin--bottom_500">
          {errorFields["tableData"] && (
            <div
              className="o-row u-margin--top_200 u-margin--bottom_100"
              style={{ color: "red" }}
            >
              {errorFields["tableData"]}
            </div>
          )}
          {errorFields["tableItems"] && (
            <div
              className="o-row u-margin--top_100 u-margin--bottom_300"
              style={{ color: "red" }}
            >
              {errorFields["tableItems"]}
            </div>
          )}

          <PaperListedTable
            columns={columns}
            initialData={tableData}
            handleAddRow={(newItems) => {
              const emptyRows = newItems.filter(isRowEmpty);

              if (emptyRows.length <= 1) {
                setTableData(newItems);
              }
            }}
            onFetchColorName={fetchColorName}
            loadingColorName={loadingProductName}
            handleSelectCurrency={setSelectedCurrency}
            selectedCurrency={selectedCurrency}
          />
        </section>
        {/* Price and payment section */}
        <section className="u-margin--bottom_200 o-column o-column--gap-size_400 u-max-width_500">
          <section className={classes.section}>
            <DiscountSection
              originalPrice={sumOfTotalAmount}
              onPriceChange={(priceValue) => {
                setPrice(priceValue);
                if (selectedInvoiceType.value === "debt") {
                  setInvoice((prev) => ({
                    ...prev,
                    receivedAmount: 0,
                    leftAmount: priceValue,
                  }));
                } else {
                  setInvoice((prev) => ({
                    ...prev,
                    receivedAmount: priceValue,
                    leftAmount: 0,
                  }));
                }
              }}
              inputMaxWidth={true}
              selectedCurrency={selectedCurrency?.value}
            />
          </section>
          <div>
            <span>{t("received")}: </span>
            <CustomInputWithUnit
              name="receivedAmount"
              onChange={(e) => {
                handleInputChange(e);
                setInvoice((prev) => ({
                  ...prev,
                  leftAmount:
                    parseFloat(sanitizeNumber(price)) - e.target.value,
                }));
              }}
              value={parseInt(invoice.receivedAmount) || 0}
              unit={t(selectedCurrency?.title?.toUpperCase() || "USD")}
              min={0}
              type="money"
            />
          </div>
          <div>
            <span>{t("left")}: </span>
            <CustomInputWithUnit
              name="leftAmount"
              readonly={true}
              onChange={handleInputChange}
              value={
                parseFloat(sanitizeNumber(invoice.leftAmount))?.toFixed(2) || 0
              }
              unit={t(selectedCurrency?.title?.toUpperCase() || "USD")}
            />
          </div>

          <section>
            <span>{t("notes")}: </span>
            <CustomInput
              type="textarea"
              name="notes"
              onChange={handleInputChange}
              value={invoice?.notes || ""}
            />
          </section>
        </section>
        {/* Loading indicator */}
        {isLoading && <Loader />}
        {/* Action buttons */}
        <section className="o-row o-row--horizontal-align_end o-row--gap-size_500 u-margin--top_200">
          <CustomButton
            title={t("saveAndPrint")}
            onClick={handleSaveAndPrint}
            icon={saveIcon}
            iconSize={16}
          />
        </section>

        <PaperInvoicePrintComponent
          ref={receiptRef}
          invoiceData={printableInvoiceData}
          invoiceDetails={invoiceDetails}
          customerData={customerData}
          creatorDetails={creatorDetails}
          currency={selectedCurrency}
        />

        <SipparLightsInvoiceTemplate
          ref={sipparInvoiceRef}
          invoiceData={{
            // Company info
            companyName: "Sippar Lights",
            companyTagline: "Trading and Services",

            // Invoice details
            invoiceNumber: invoiceDetails?.invoiceNumber,
            date: invoiceDetails?.invoiceDate,

            // Customer and seller info
            sellerName: "SIPPAR LIGHTS",
            sellerLocation: "IRAQ - BAGHDAD",
            buyerName: customerData?.customerName,
            buyerLocation: customerData?.address,

            // Financial data
            totalPrice: invoiceDetails?.invoiceAmount?.totalPrice || 0,
            finalPrice: invoiceDetails?.invoiceAmount?.finalAmount || 0,
            discountPercentage: invoiceDetails?.invoiceAmount?.discount
              ? (
                  (invoiceDetails.invoiceAmount.discount /
                    invoiceDetails.invoiceAmount.totalPrice) *
                  100
                ).toFixed(0)
              : 0,

            // Items - map your invoice data to the expected format
            items:
              printableInvoiceData?.map((item) => ({
                description:
                  `${item[0]?.value} ${item[2]?.value.split(" ")[0]}cm ${
                    item[3]?.value.split(" ")[0]
                  }gsm (${item[1]?.value.split(" ")[0]} roll)` || "-",
                quantity:
                  `${parseFloat(item[4]?.value.split(" ")[0])} TON` || "-",
                pricePerTon:
                  parseFloat(item[5]?.value?.replace(/[^\d.]/g, "")) || 0,
                totalPrice:
                  parseFloat(item[6]?.value?.replace(/[^\d.]/g, "")) || 0,
              })) || [],

            // Calculate total tons from your data
            totalTons:
              printableInvoiceData
                ?.reduce((total, item) => {
                  const quantity =
                    item[4]?.value?.replace(/[^\d.]/g, "") || "0";
                  return total + parseFloat(quantity);
                }, 0)
                .toFixed(5) + " TON",
          }}
        />
      </div>
    </PageWrapper>
  );
};

export default PaperCustomersCashier;
